import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Eye, Heart, Crown, Users, Handshake, Award, Megaphone, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { values } from '../data/mock';

const iconMap = {
  Crown: Crown,
  Users: Users,
  Handshake: Handshake,
  Megaphone: Megaphone,
  Award: Award,
};

const valueColors = [
  { bg: 'from-brand-gold to-gold-600', shadow: 'shadow-gold-500/20' },
  { bg: 'from-brand-green to-green-600', shadow: 'shadow-green-500/20' },
  { bg: 'from-brand-red to-red-600', shadow: 'shadow-red-500/20' },
  { bg: 'from-brand-navy to-navy-700', shadow: 'shadow-navy-500/20' },
  { bg: 'from-brand-gold to-gold-600', shadow: 'shadow-gold-500/20' },
];

export default function MissionPage() {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-navy-50 via-white to-gold-50/30">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-0 w-96 h-96 bg-gold-100/30 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <nav className="flex items-center gap-2 text-sm text-navy-500 mb-6">
              <Link to="/" className="hover:text-brand-green transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-navy-900 font-medium">Mission & Vision</span>
            </nav>
            
            <h1 className="text-4xl sm:text-5xl font-bold text-navy-900 mb-6">
              Our Mission,{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-green to-green-600">
                Vision & Values
              </span>
            </h1>
            <p className="text-lg text-navy-600 leading-relaxed">
              Guided by our commitment to equity, inclusion, and excellence in early childhood education.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold-50 border border-gold-200 mb-6">
                <Target className="w-4 h-4 text-brand-gold" />
                <span className="text-sm font-medium text-gold-700">Our Mission</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 mb-6">Mission</h2>
              <p className="text-lg text-navy-600 leading-relaxed">
                DC Men in ECE is committed to empowering men of color in Early Childhood Education by enhancing their recruitment, retention, and advancement in Washington D.C. Through a supportive community, professional development, and resource sharing, our mission is to promote their growth and transform educational outcomes, enriching children's learning experiences and fostering a diverse educational landscape.
              </p>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-brand-green to-green-600 rounded-3xl p-8 shadow-2xl shadow-green-500/20">
                <Target className="w-16 h-16 text-white/80 mb-6" />
                <blockquote className="text-xl text-white font-medium leading-relaxed">
                  "Empowering men of color to transform early childhood education through community, development, and advocacy."
                </blockquote>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-24 bg-navy-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="bg-navy-800/50 border border-navy-700/50 rounded-3xl p-8">
                <Eye className="w-16 h-16 text-brand-gold/80 mb-6" />
                <blockquote className="text-xl text-white font-medium leading-relaxed">
                  "A future where the Early Childhood Education workforce mirrors the diversity of our community."
                </blockquote>
              </div>
            </div>
            
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-gold/10 border border-brand-gold/20 mb-6">
                <Eye className="w-4 h-4 text-brand-gold" />
                <span className="text-sm font-medium text-brand-gold">Our Vision</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">Vision</h2>
              <p className="text-lg text-navy-200 leading-relaxed">
                Our vision is a future where the Early Childhood Education workforce mirrors the diversity of our community, with men of color playing a significant role in shaping educational practices and outcomes. We aspire to transform the landscape of ECE in D.C. by advocating for inclusivity, providing continuous learning opportunities, and fostering a supportive network that empowers men of color to achieve excellence in their educational careers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-50 border border-red-200 mb-6">
              <Heart className="w-4 h-4 text-brand-red" />
              <span className="text-sm font-medium text-red-700">Our Values</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 mb-4">What We Stand For</h2>
            <p className="text-navy-600">Our core values guide everything we do at DC Men in ECE.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((value, index) => {
              const Icon = iconMap[value.icon] || Award;
              const colors = valueColors[index % valueColors.length];
              return (
                <Card key={index} className="group border-0 shadow-lg shadow-navy-100 hover:shadow-xl hover:shadow-navy-100/50 transition-all duration-500">
                  <CardContent className="p-8">
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${colors.bg} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg ${colors.shadow}`}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-navy-900 mb-3">{value.title}</h3>
                    <p className="text-navy-600 leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Theory of Change */}
      <section className="py-24 bg-gradient-to-br from-navy-50 to-gold-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 mb-4">Theory of Change</h2>
            <p className="text-navy-600">Our strategic framework for creating lasting impact in early childhood education.</p>
          </div>
          
          <div className="bg-white rounded-3xl shadow-xl p-6 md:p-10">
            <img 
              src="/images/theory-of-change-1.png" 
              alt="DC Men in ECE Theory of Change"
              className="w-full h-auto rounded-xl"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 mb-6">
            Ready to Join Our Mission?
          </h2>
          <p className="text-lg text-navy-600 mb-10">
            Be part of the movement transforming early childhood education.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/membership">
              <Button size="lg" className="bg-brand-green hover:bg-green-600 text-white font-semibold px-8 shadow-xl shadow-green-500/20">
                Become a Member
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-2 border-navy-200 text-navy-700 font-semibold px-8 hover:bg-navy-50">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
